import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib/auth/useAuth';
import { getOrganizationDetails, updateOrganizationDetails, listOrganizations, type OrganizationDetails, type OrganizationMetadata, type OrganizationUpdateRequest } from '@/api/organizations';
import { isEvalOpsStaff } from '@/lib/utils/user';

interface OrganizationContextType {
  organization: OrganizationDetails | null;
  loading: boolean;
  error: string | null;
  updateMetadata: (metadata: OrganizationMetadata, name?: string) => Promise<void>;
  refetchOrganization: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Helper function to get cached organization data
const getCachedOrganization = (): OrganizationDetails | null => {
  try {
    const cached = localStorage.getItem('cached_organization');
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// Helper function to set cached organization data
const setCachedOrganization = (org: OrganizationDetails | null) => {
  if (org) {
    localStorage.setItem('cached_organization', JSON.stringify(org));
  } else {
    localStorage.removeItem('cached_organization');
  }
};

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user: authUser, loading: authLoading } = useAuth();
  
  // Extract primitive values to avoid object reference dependencies
  const authUserId = authUser?.id;
  const authUserEmail = authUser?.email;
  const authUserRawId = authUser?.raw_user?.id;
  const authUserOrgId = authUser?.raw_user?.organization_membership?.organization_id;
  const [organization, setOrganization] = useState<OrganizationDetails | null>(getCachedOrganization());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add request deduplication
  const [currentRequest, setCurrentRequest] = useState<Promise<void> | null>(null);

  const fetchOrganization = useCallback(async () => {
    // Prevent duplicate requests
    if (currentRequest) {
      return currentRequest;
    }

    const request = (async () => {
      try {
        const isStaff = authUserEmail ? isEvalOpsStaff(authUserEmail) : false;
        const selectedOrgId = localStorage.getItem('selected_organization_id');
        const orgId = selectedOrgId || authUserOrgId;
        
        // Simplified logic: prefer specific org ID, fallback to list for staff
        if (orgId && !isStaff) {
          // Direct org lookup for non-staff users
          const result = await getOrganizationDetails(orgId);
          if (result.success && result.data) {
            setOrganization(result.data);
            setCachedOrganization(result.data);
            setError(null);
            return;
          }
        }

        // For staff users or when direct lookup fails, use list endpoint
        const listResult = await listOrganizations();
        if (listResult.success && listResult.data?.organizations?.length > 0) {
          let selectedOrg = listResult.data.organizations[0]; // Default to first
          
          if (orgId) {
            // Try to find specific org if we have an ID
            const matchingOrg = listResult.data.organizations.find(org => org.id === orgId);
            if (matchingOrg) {
              selectedOrg = matchingOrg;
            }
          }
          
          setOrganization(selectedOrg);
          setCachedOrganization(selectedOrg);
          setError(null);
          return;
        }

        // Handle no organizations case gracefully
        console.warn('[Organization] No organizations found for user');
        setOrganization(null);
        setCachedOrganization(null);
        setError(null); // Don't treat this as an error
        return;
      } catch (err) {
        console.error('[Organization] Error fetching organization:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch organization details');
        
        // Use cached data as fallback
        const cachedOrg = getCachedOrganization();
        if (cachedOrg) {
          setOrganization(cachedOrg);
        } else {
          setOrganization(null);
          setCachedOrganization(null);
        }
      } finally {
        setLoading(false);
        setCurrentRequest(null);
      }
    })();

    setCurrentRequest(request);
    return request;
  }, [authUserId, authUserEmail, authUserOrgId]);

  const updateMetadata = useCallback(async (metadata: OrganizationMetadata, name?: string) => {
    if (!organization?.id) {
      throw new Error('No organization selected');
    }

    try {
      const updateRequest: OrganizationUpdateRequest = {
        name: name || organization.name,
        metadata: {
          ...organization.metadata,
          ...metadata
        }
      };
      
      const result = await updateOrganizationDetails(organization.id, updateRequest);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to update organization metadata');
      }

      const updatedOrg = { ...organization, ...result.data };
      setOrganization(updatedOrg);
      setCachedOrganization(updatedOrg);
      setError(null);
    } catch (err) {
      console.error('Error updating organization metadata:', err);
      setError(err instanceof Error ? err.message : 'Failed to update organization metadata');
      throw err;
    }
  }, [organization]);

  const refetchOrganization = useCallback(async () => {
    await fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    if (!authLoading && authUser) {
      fetchOrganization();
    }
  }, [authLoading, authUserId, fetchOrganization]);

  const value = useMemo(() => ({
    organization,
    loading,
    error,
    updateMetadata,
    refetchOrganization
  }), [organization, loading, error, updateMetadata, refetchOrganization]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
} 